import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { SessionService } from '@monsido/ng2/core/session/session.service';

import { MyExportsDialogService } from '@client/app/shared-modules/my-exports/services/my-exports-dialog.service';
import { CreateExportRepoService } from '@client/app/shared-modules/create-export/services/create-export.repo.service';
import { CreateExport } from '@client/app/shared-modules/create-export/types/create-export.type';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';

@Component({
    selector: 'mon-create-export-dialog',
    imports: [
        CommonModule,
        TranslocoModule,
        ButtonModule,
        ProgressSpinnerModule,
    ],
    templateUrl: './create-export-dialog.component.html',
    styleUrls: ['./create-export-dialog.component.scss'],
})
export class CreateExportDialogComponent {
    loading = false;
    exportCreated = false;
    statusMessage = '';

    @ViewChild('spinnerRef') private readonly spinnerRef?: ElementRef<HTMLElement>;
    @ViewChild('exportButtonRef') private readonly exportButtonRef?: ElementRef<HTMLElement>;
    @ViewChild('successButtonRef') private readonly successButtonRef?: ElementRef<HTMLElement>;

    constructor (
        private readonly dialogRef: DynamicDialogRef,
        private readonly dialogConfig: DynamicDialogConfig,
        private readonly createExportRepoService: CreateExportRepoService,
        private readonly sessionService: SessionService,
        private readonly myExportsDialogService: MyExportsDialogService,
        private readonly translocoService: TranslocoService,
        private readonly globalToastMessageService: GlobalToastMessageService,
    ) {}

    get config (): CreateExport {
        return this.dialogConfig.data?.config;
    }

    close (): void {
        this.dialogRef.close();
    }

    async createExport (): Promise<void> {
        this.statusMessage = this.translocoService.translate('createExport.dialog.status.creating');
        this.loading = true;
        setTimeout(() => this.spinnerRef?.nativeElement?.focus(), 0);
        let sourceType: 'Domain' | 'DomainGroup' | undefined = undefined;
        if (this.config.payload.isGlobalExport) {
            if (this.sessionService.domainGroup) {
                sourceType = 'DomainGroup';
            } else if (this.sessionService.domain) {
                sourceType = 'Domain';
            } else {
                sourceType = undefined;
            }
        }

        if (this.config.payload.source_type &&
            (this.config.payload.source_type === 'Domain' || this.config.payload.source_type === 'DomainGroup')
        ) {
            sourceType = this.config.payload.source_type;
        }

        try {
            const payload: CreateExport['payload'] = {
                recipients: [this.sessionService.me?.email ?? ''],
                category: this.config.payload.category,
            };

            if (this.config.payload.category_ref !== undefined) {
                payload.category_ref = this.config.payload.category_ref;
            }
            if (sourceType !== undefined) {
                payload.source_type = sourceType;
            }
            if (this.config.payload.source_id !== undefined) {
                payload.source_id = this.config.payload.source_id;
            }
            if (this.config.payload.params !== undefined) {
                payload.params = this.config.payload.params;
            }

            await this.createExportRepoService.createExport(payload);
            this.exportCreated = true;
            this.statusMessage = this.translocoService.translate('createExport.dialog.status.created');
            setTimeout(() => this.successButtonRef?.nativeElement?.focus(), 0);

            this.dialogConfig.header = this.translocoService.translate('createExport.dialog.exportInProgress.header');
        } catch {
            this.exportCreated = false;
            this.globalToastMessageService.error(
                this.translocoService.translate('createExport.dialog.toast.createErrorMessage'),
            );
            this.statusMessage = this.translocoService.translate('createExport.dialog.status.error');
            setTimeout(() => this.exportButtonRef?.nativeElement?.focus(), 0);
        } finally {
            this.loading = false;
        }
    }

    openMyExports (): void {
        this.close();
        void this.myExportsDialogService.open();
    }
}
